import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/baseUi/dialog"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxProgramDetailsEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import { useBoxSlotTemplateDelete } from "@/modules/boxSlotTemplate/hooks/useBoxSlotTemplates"
import { CalendarIcon } from "@images/box/calendarIcon"
import { type BoxSlotTemplateEntity } from "@modules/boxSlotTemplate/entities/boxSlotTemplateEntity"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useCallback, useImperativeHandle, useState } from "react"

export type BoxSlotTemplateFormDeleteModalRef = {
  open: () => void
}

type BoxSlotTemplateFormDeleteModalProps = {
  boxSlotTemplate: BoxSlotTemplateEntity
  boxProgram: BoxProgramDetailsEntity
  ref: React.Ref<BoxSlotTemplateFormDeleteModalRef>
}

export function BoxSlotTemplateFormDeleteModal({
  boxSlotTemplate,
  boxProgram,
  ref,
}: BoxSlotTemplateFormDeleteModalProps) {
  const { mutateAsync: deleteBoxSlotTemplate } = useBoxSlotTemplateDelete()
  const [showModal, setShowModal] = useState(false)
  const [isSubmit, setIsSubmit] = useState(false)
  const [isSelected, setIsSelected] = useState({
    now: false,
    future: false,
  })
  const { hideSidebar } = useSidebar()

  const onHide = useCallback(() => {
    setShowModal(false)
    setIsSubmit(false)
  }, [])

  const onSubmit = async () => {
    setIsSubmit(true)
    await deleteBoxSlotTemplate({
      boxProgramId: boxProgram.id,
      slotTemplateId: boxSlotTemplate.id,
      impactAlreadyCreatedSlots: isSelected.now,
    })
      .then(() => {
        toast.success(
          isSelected.now
            ? word("box.slotTemplates.form.modalDelete.toast.now")
            : word("box.slotTemplates.form.modalDelete.toast.future"),
          {
            classNames: successToastClassName(),
          },
        )
      })
      .catch((e) => {
        console.error(LoggerType.Box + "delete template slot", e)
        toast.error(word("box.slotTemplates.form.modalDelete.toast.error"), {
          classNames: errorToastClassName(),
        })
      })
      .finally(() => {
        onHide()
        hideSidebar()
      })
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const dateForTwoWeek = new Date()
  dateForTwoWeek.setDate(dateForTwoWeek.getDate() + 15)

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="mt-3 flex w-[512px] flex-col rounded-4xl">
        {/* HEADER */}
        <DialogHeader className="flex flex-col flex-wrap content-center justify-center gap-2 pt-5 text-center">
          <div className="flex w-full content-center justify-center">
            <CalendarIcon color="currentColor" />
          </div>
          <Text fontStyle="TL" className="text-textDefault-heading">
            {word("box.slotTemplates.form.modal.header.title")}
          </Text>
          <div className="flex flex-wrap content-center justify-center gap-2">
            <Text fontStyle="LL" className="rounded-full bg-neutral-100 px-3 py-2 text-textDefault-body">
              {word("box.slotTemplates.form.modal.tag.slotDeletion")}
            </Text>
          </div>
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("box.slotTemplates.form.modal.header.subtitle")}
          </Text>
        </DialogHeader>

        {/* CONTENT */}
        <Text fontStyle="BM" className="mt-5 flex text-center text-textDefault-caption">
          {word("box.slotTemplates.form.modalDelete.content.title")}
        </Text>
        <div className="mt-3 flex gap-3">
          <div
            className={`flex w-6/12 cursor-pointer flex-col gap-3 rounded-2xl border p-3 ${isSelected.now ? "border-primary-400" : "cursor-pointer border-neutral-700"}`}
            onClick={() => setIsSelected({ now: true, future: false })}
          >
            <Text fontStyle="TS" className="text-center text-textDefault-body">
              {word("box.slotTemplates.form.modalDelete.content.now")}
            </Text>
            <div className="flex flex-col gap-2">
              <Text fontStyle="BS" className="text-center text-textDefault-caption">
                {word("box.slotTemplates.form.modal.content.athele")}
              </Text>
              <Text fontStyle="BS" className="text-center text-textDefault-caption">
                {word("box.slotTemplates.form.modal.content.notification")}
              </Text>
              <Text fontStyle="BS" className="text-center text-textDefault-caption">
                {word("box.slotTemplates.form.modal.content.email")}
              </Text>
              <Text fontStyle="BS" className="text-center text-textDefault-caption">
                {word("box.slotTemplates.form.modal.content.refund")}
              </Text>
            </div>
          </div>
          <div
            className={`flex w-6/12 cursor-pointer flex-col gap-3 rounded-2xl border p-3 ${isSelected.future ? "border-primary-400" : "cursor-pointer border-neutral-700"}`}
            onClick={() => setIsSelected({ now: false, future: true })}
          >
            <Text fontStyle="TS" className="text-center text-textDefault-body">
              {word("box.slotTemplates.form.modalDelete.content.future")}
            </Text>
            <Text fontStyle="BS" className="text-center text-textDefault-caption">
              {` ${word("box.slotTemplates.form.modal.content.starting")} ${formatDateWithHyphen(dayjs(dateForTwoWeek).format("L"))}`}
            </Text>
          </div>
        </div>
        {/* BUTTON */}
        <div className="mt-5 flex flex-row content-center justify-center gap-2">
          <Button variant="outline" size="lg" onClick={() => onHide()} className="flex-1">
            {word("global.cancel")}
          </Button>
          <Button
            size="lg"
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={(!isSelected.future && !isSelected.now) || isSubmit}
            className="flex-1"
          >
            {word("global.check")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
